import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { Loader2, Save, ShieldCheck, X, AlertCircle, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import { hasPremiumAccess } from '../config/subscriptions';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * AIRecipeGeneratorModal
 * ──────────────────────
 * Phase D of the Smart-Search cascade: when the search returns 0 high-confidence
 * results and `ai_eligible:true`, this modal asks GPT to invent a single
 * structured recipe matching the user's query and lets them either:
 *   • Gem privat       – saves to their own private collection (works for
 *                        guests too, if they're below the 2-recipe limit)
 *   • Send til Sandbox – publishes pending admin moderation (logged-in only)
 *   • Kassér           – throws the draft away, no persistence
 *
 * Field mapping from AI draft → RecipeCreate happens client-side and is
 * intentionally conservative — anything we can't safely map gets a sensible
 * default rather than failing the save.
 */

const SESSION_KEY = 'session_id';

const getSessionId = () => {
  let sid = localStorage.getItem(SESSION_KEY);
  if (!sid) {
    sid = `guest-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
    localStorage.setItem(SESSION_KEY, sid);
  }
  return sid;
};

const ALLOWED_TYPES = new Set([
  'klassisk', 'juice', 'smoothie', 'sodavand', 'cocktail',
  'kaffe', 'sport', 'sukkerfri', 'maelk',
]);

const mapAIDraftToRecipeCreate = (ai, { isPublished, sessionId, validation }) => {
  const recipe = ai?.recipe || {};

  const aiIngredients = Array.isArray(recipe.ingredients) ? recipe.ingredients : [];
  const ingredients = aiIngredients
    .filter((i) => i && (i.name || i.ingredient_name))
    .map((i) => ({
      name: (i.name || i.ingredient_name || '').trim(),
      category_key: (i.category || i.category_key || 'other').toLowerCase().trim(),
      quantity: Number.isFinite(Number(i.amount)) ? Number(i.amount) : Number(i.quantity) || 0,
      unit: (i.unit || 'ml').trim(),
      role: i.required === false ? 'optional' : 'required',
      brix: i.brix === null || i.brix === undefined ? null : Number(i.brix),
    }));

  const procedure = Array.isArray(recipe.procedure) ? recipe.procedure : [];
  const steps = procedure
    .map((s) => (typeof s === 'string' ? s.trim() : ''))
    .filter((s) => s.length > 0);

  // Clamp `type` to the allowed enum — AI sometimes invents categories like
  // "alkoholdrik" or "sommer-cocktail" which then render as empty in the
  // edit-recipe select.
  const rawType = (recipe.type || 'klassisk').toLowerCase().trim();
  const safeType = ALLOWED_TYPES.has(rawType) ? rawType : 'klassisk';

  // Prefer the validator's CALCULATED brix/alcohol over the AI's target,
  // so the saved recipe matches what the modal showed the user. Machine
  // analysis (`/admin/analyze`, `/recipes/{id}/machine-check`) depends on
  // `target_brix` reflecting reality — without this, a 13°Bx Frosé draft
  // ends up stored as 14°Bx (the AI's default) and the machine check
  // flags critical errors for no real reason.
  const computedBrix = Number(validation?.total_brix);
  const computedAlc = Number(validation?.alcohol_percentage);
  const targetBrix = Number.isFinite(computedBrix) && computedBrix > 0
    ? computedBrix
    : (Number(recipe.brix || recipe.target_brix) || 13.0);
  const alcoholPercentage = Number.isFinite(computedAlc) ? computedAlc : 0;
  const alcoholFlag = alcoholPercentage > 0 || !!(recipe.contains_alcohol || recipe.alcohol_flag);

  return {
    session_id: sessionId,
    name: (recipe.name || 'AI-genereret opskrift').slice(0, 120),
    description: (recipe.description || 'Genereret af AI på baggrund af din søgning.').slice(0, 800),
    image_url: '/api/images/placeholder.jpg',
    // `base_volume_ml` MUST be an integer — Pydantic's `int_from_float` rejects
    // fractional values (e.g. 2563.1 after an auto-adjust). Round here so the
    // payload survives validation without losing the validator's authority.
    base_volume_ml: Math.round(
      Number(validation?.total_volume_ml) || Number(recipe.base_volume_ml) || 2000
    ),
    target_brix: targetBrix,
    alcohol_flag: alcoholFlag,
    alcohol_percentage: alcoholPercentage,
    color: recipe.color || 'red',
    type: safeType,
    // `family` is the new fine-grained profile label (Margarita, Frappé, …).
    // The AI sets it on JSON when its prompt detects a profile match. We pass
    // it straight through — empty / missing on the AI response means no
    // family was inferred, which is fine (frontend hides the badge).
    family: (recipe.family || '').slice(0, 80) || null,
    tags: Array.isArray(recipe.tags) ? recipe.tags : [],
    ingredients,
    steps,
    is_published: isPublished,
  };
};

/**
 * Convert any backend `detail` shape (string, FastAPI 422 array, plain object)
 * into a single, user-friendly Danish message. Without this, FastAPI's 422
 * payload `{detail: [{type, loc, msg, …}]}` ends up in the UI as the literal
 * string "[object Object]".
 */
const humanizeDetail = (detail) => {
  if (!detail) return '';
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    // FastAPI 422 validation errors → look for a known type and translate.
    const tooLong = detail.find((d) => d?.type === 'string_too_long');
    if (tooLong) {
      return 'Din søgning blev for lang for AI\u2019en. Prøv at gøre den kortere eller fjern noget af det ekstra detalje-tekst.';
    }
    // Fall back to joining each entry's `msg` field.
    const msgs = detail
      .map((d) => (d && typeof d === 'object' ? d.msg || d.message : String(d)))
      .filter(Boolean);
    if (msgs.length) return msgs.join('. ');
  }
  if (typeof detail === 'object') {
    return detail.message || detail.msg || JSON.stringify(detail);
  }
  return String(detail);
};

const AIRecipeGeneratorModal = ({ open, onClose, query, user, onSavedPrivate, onSentToSandbox }) => {
  const [loadingState, setLoadingState] = useState('idle'); // idle | generating | error
  const [errorMsg, setErrorMsg] = useState('');
  const [errorSuggestions, setErrorSuggestions] = useState([]);
  const [refinement, setRefinement] = useState('');
  const [draft, setDraft] = useState(null);
  const [validation, setValidation] = useState(null);
  const [validating, setValidating] = useState(false);
  const [saving, setSaving] = useState(null); // null | 'private' | 'sandbox'

  const isGuest = !hasPremiumAccess(user);
  const canUseAI = ['pro', 'editor', 'admin'].includes(user?.role);

  const validateDraft = useCallback(async (draftPayload) => {
    if (!draftPayload?.recipe?.ingredients?.length) return;
    setValidating(true);
    try {
      const res = await axios.post(`${API}/ai/validate-recipe-draft`, {
        ingredients: draftPayload.recipe.ingredients,
        base_volume_ml: draftPayload.recipe.base_volume_ml,
        target_brix: draftPayload.recipe.brix || draftPayload.recipe.target_brix || 13.0,
      }, { timeout: 15_000 });
      setValidation(res.data);
    } catch (e) {
      // Validation failures are non-fatal — the draft is still usable, the
      // user just won't see the freezability badge.
      console.warn('validate-recipe-draft failed:', e?.response?.status, e?.message);
      setValidation(null);
    } finally {
      setValidating(false);
    }
  }, []);

  const applyAutoAdjustment = () => {
    if (!validation?.suggested_adjustment || !draft?.recipe) return;
    const adj = validation.suggested_adjustment;
    const newIngredients = [...(draft.recipe.ingredients || [])];
    // If the adjustment ingredient already exists, increment it; else append.
    const idx = newIngredients.findIndex(
      (i) => (i.name || '').toLowerCase() === adj.ingredient.toLowerCase()
    );
    if (idx >= 0) {
      const cur = Number(newIngredients[idx].amount || newIngredients[idx].quantity || 0);
      newIngredients[idx] = {
        ...newIngredients[idx],
        amount: Math.round((cur + adj.ml_to_add) * 10) / 10,
      };
    } else {
      newIngredients.push({
        name: adj.ingredient,
        amount: Math.round(adj.ml_to_add * 10) / 10,
        unit: 'ml',
        brix: adj.ingredient.toLowerCase().startsWith('vand') ? 0 : 65,
        required: false,
      });
    }
    const newRecipe = {
      ...draft.recipe,
      ingredients: newIngredients,
      base_volume_ml: (Number(draft.recipe.base_volume_ml) || 2000) + Math.round(adj.ml_to_add),
    };
    const newDraft = { ...draft, recipe: newRecipe };
    setDraft(newDraft);
    toast.success(`Tilføjet ${adj.ml_to_add} ml ${adj.ingredient}. Genberegner…`);
    // Re-validate after applying the adjustment so the badge updates.
    validateDraft(newDraft);
  };

  const callGenerator = useCallback(async (extraHint = '') => {
    setLoadingState('generating');
    setErrorMsg('');
    setErrorSuggestions([]);
    setDraft(null);
    try {
      // The /api/ai/create-recipe prompt treats bare topic queries (e.g. "frosé",
      // "lakridsis med bjørnebær") as ambiguous and returns `need-more-info`.
      // Smart-search invokes us with exactly those topic-only queries, so we
      // re-frame them as an explicit recipe request before handing off to the
      // LLM. The framing ALSO instructs the model to treat any concrete
      // ingredient/flavour the user wrote as BINDING — otherwise GPT happily
      // ignores them (e.g. "Frozé rosévin" used to come back as a fruit slush
      // with zero wine). If the AI can't fit a binding ingredient, it must
      // return need-more-info rather than silently substituting.
      const hint = extraHint ? `\n\nEkstra detaljer fra brugeren: ${extraHint}` : '';
      const framedQuery = [
        `Brugeren søgte efter: "${query}"${hint}`,
        '',
        'OPGAVE: Lav en komplet slushice-opskrift der matcher brugerens søgning.',
        '',
        'BINDENDE KRAV (MEGET VIGTIGT):',
        '1. Identificér enhver KONKRET ingrediens, drik eller smag som brugeren nævner i søgningen ovenfor (fx "rosévin", "Malibu", "banan", "Baileys", "kaffe", "lakrids", "mango", "Frosé" = rosévin, "Daiquiri" = rom, "Margarita" = tequila, "Mojito" = rom+mynte+lime, osv.).',
        '2. ALLE sådanne nævnte ingredienser SKAL indgå i opskriften som påkrævede ingredienser (required: true).',
        '3. INGREDIENS-OPSLAG: For hvert bindende krav SKAL du fleksibelt matche imod ingrediens-listen ovenfor. Brug også synonymerne i "(også: ...)" til at matche. Brug det BEDSTE alternativ hvis den eksakte ordlyd ikke findes:',
        '   • "rom" → en hvilken som helst rom-variant (Hvid rom, Lys rom, Mørk Rom 40% osv.)',
        '   • "banan" → "Banan" ELLER "Banan Sirup"',
        '   • "kaffe" → "Kaffe", "Kaffe Sirup", "Cold brew kaffe" osv.',
        '   • "Malibu" → "Hvid rom" + "Kokos Sirup" (Malibu = kokos-rom)',
        '   • "Baileys" → "Baileys" (eksakt match)',
        '   • "lime" → "Lime Sirup" eller "Lime juice"',
        '   • "Frosé"/"frozen rosé"/"rosé" → "Rosévin"',
        '   Match på fornuftig vis. Brug kun need-more-info hvis INGEN tilnærmelse er mulig.',
        '4. Du må gerne supplere med yderligere ingredienser efter eget skøn så længe de bindende krav er opfyldt.',
        '5. Hvis søgningen kun er en vag stilart eller smag (fx "tropisk", "sommerlig", "rød"), må du frit improvisere — så er der ingen bindende ingredienser.',
        '6. Hvis du ER nødt til at returnere need-more-info, OVERSKRIV den standard-besked fra Regel 0 i system-prompten. Returnér i stedet en specifik besked: nævn KONKRET hvilken ingrediens der mangler og foreslå 2-3 alternative ingredienser fra ingrediens-listen. F.eks. "Ingrediensen \'rosévin\' findes ikke. Vil du bruge Prosecco eller Rose Sirup som alternativ?" — IKKE den generiske "Hvilken smag vil du have?" som er forbeholdt helt vage forespørgsler.',
        '',
        'Returnér den fulde opskrift med navn, beskrivelse, ingredienser med mængder, og procedure.',
      ].join('\n');

      const res = await axios.post(`${API}/ai/create-recipe`, {
        query: framedQuery,
        language: 'da',
      }, { timeout: 90_000 });
      if (!res.data?.success || !res.data?.recipe) {
        const fallbackMsg = res.data?.message || 'AI kunne ikke generere en opskrift. Prøv at omformulere din søgning.';
        setErrorMsg(fallbackMsg);
        setErrorSuggestions(Array.isArray(res.data?.suggestions) ? res.data.suggestions : []);
        setLoadingState('error');
        return;
      }
      setDraft(res.data);
      setValidation(null);
      setRefinement('');
      setLoadingState('idle');
      // Fire-and-forget freezability validation. The modal renders the
      // status badge once it lands; we don't block the success state on it.
      validateDraft(res.data).catch((e) => console.warn('validate skipped:', e?.message));
    } catch (err) {
      const status = err.response?.status;
      const rawDetail = err.response?.data?.detail;
      const humanDetail = humanizeDetail(rawDetail);
      // Build a friendly, single-string Danish error. We deliberately do NOT
      // surface the HTTP status to end-users by default — they don't care
      // about 422 vs 500. The fallback wording matches the user's spec:
      // "Vi kunne ikke lave opskriften endnu, fordi en nødvendig ingrediens
      // mangler. Prøv at tilføje flere detaljer eller vælg et af forslagene."
      let message;
      if (humanDetail) {
        message = humanDetail;
      } else if (status === 422) {
        message = 'Vi kunne ikke lave opskriften endnu, fordi en nødvendig ingrediens mangler eller søgningen var for lang. Prøv at tilføje flere detaljer eller vælg et af forslagene.';
      } else if (status && status >= 500) {
        message = 'Der gik noget galt på serveren under generering. Prøv igen om et øjeblik.';
      } else {
        message = 'Vi kunne ikke lave opskriften endnu. Prøv at tilføje flere detaljer eller vælg et af forslagene.';
      }
      setErrorMsg(message);
      // Preserve any backend-suggested chips even on a hard error.
      const errSuggestions = err.response?.data?.suggestions;
      setErrorSuggestions(Array.isArray(errSuggestions) ? errSuggestions : []);
      setLoadingState('error');
    }
  }, [query]);

  useEffect(() => {
    if (open && query && canUseAI) {
      callGenerator('');
    }
  }, [open, query, callGenerator, canUseAI]);

  const handleClose = () => {
    setDraft(null);
    setValidation(null);
    setSaving(null);
    setErrorMsg('');
    setErrorSuggestions([]);
    setRefinement('');
    setLoadingState('idle');
    onClose();
  };

  const handleSave = async (mode) => {
    if (!draft || saving) return;
    if (mode === 'sandbox' && isGuest) {
      toast.error('Du skal være logget ind for at sende til Sandbox.');
      return;
    }
    setSaving(mode);
    try {
      const payload = mapAIDraftToRecipeCreate(draft, {
        isPublished: mode === 'sandbox',
        sessionId: getSessionId(),
        validation,
      });
      const res = await axios.post(`${API}/recipes`, payload);
      if (mode === 'private') {
        toast.success('Opskrift gemt privat. Du finder den på Mine Opskrifter.');
        onSavedPrivate?.(res.data);
      } else {
        toast.success('Sendt til Sandbox. En admin gennemgår den snarest.');
        onSentToSandbox?.(res.data);
      }
      handleClose();
    } catch (err) {
      const detail = err.response?.data?.detail || err.message;
      toast.error(`Kunne ikke gemme: ${detail}`);
    } finally {
      setSaving(null);
    }
  };

  const recipe = draft?.recipe;

  return (
    <Dialog open={open} onOpenChange={(v) => !v && handleClose()}>
      <DialogContent
        className="max-w-2xl max-h-[90vh] overflow-y-auto"
        data-testid="ai-recipe-modal"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-purple-500" />
            AI-genereret opskrift
          </DialogTitle>
          <DialogDescription>
            Baseret på din søgning: <span className="font-semibold">&quot;{query}&quot;</span>
          </DialogDescription>
        </DialogHeader>

        {!canUseAI && (
          <div className="py-6 space-y-4" data-testid="ai-recipe-pro-lock">
            <div className="bg-gradient-to-br from-purple-50 to-pink-50 border border-purple-200 rounded-xl p-5 text-center">
              <Sparkles className="w-10 h-10 mx-auto text-purple-500 mb-3" />
              <h3 className="font-bold text-lg text-gray-900 mb-2">SlushBook AI er en Pro-funktion</h3>
              <p className="text-sm text-gray-700 mb-4">
                Få AI til at finde på nye opskrifter ud fra dine søgeord —
                inkl. automatisk Brix-balance og frysbarheds-tjek. Funktionen er
                tilgængelig for Pro, Editor og Admin.
              </p>
              <div className="flex flex-col sm:flex-row gap-2 justify-center">
                <Button variant="outline" onClick={handleClose} data-testid="ai-pro-lock-close">
                  Luk
                </Button>
                <Button
                  onClick={() => {
                    handleClose();
                    if (isGuest) {
                      window.location.assign('/login?next=/recipes');
                    } else {
                      window.location.assign('/profile?upgrade=pro');
                    }
                  }}
                  data-testid="ai-pro-lock-upgrade"
                  className="bg-gradient-to-r from-purple-500 to-pink-500 hover:opacity-90"
                >
                  {isGuest ? 'Log ind / Opret konto' : 'Se abonnementer'}
                </Button>
              </div>
            </div>
          </div>
        )}

        {canUseAI && loadingState === 'generating' && (
          <div className="py-10 flex flex-col items-center gap-3" data-testid="ai-recipe-loading">
            <Loader2 className="w-8 h-8 text-purple-500 animate-spin" />
            <p className="text-sm text-gray-600">AI&apos;en arbejder på en opskrift…</p>
            <p className="text-xs text-gray-400">Det tager normalt 5-20 sekunder.</p>
          </div>
        )}

        {canUseAI && loadingState === 'error' && (
          <div className="py-6 space-y-3" data-testid="ai-recipe-error">
            <div className="flex items-start gap-3 bg-amber-50 border border-amber-200 rounded-lg p-4">
              <AlertCircle className="w-5 h-5 text-amber-600 mt-0.5 flex-shrink-0" />
              <div className="flex-1">
                <p className="font-semibold text-amber-800 mb-1">AI&apos;en har brug for lidt mere</p>
                <p className="text-sm text-amber-700">{errorMsg}</p>
                {errorSuggestions.length > 0 && (
                  <div className="mt-3 flex flex-wrap gap-2" data-testid="ai-recipe-error-suggestions">
                    {errorSuggestions.map((s) => (
                      <button
                        type="button"
                        key={s}
                        onClick={() => setRefinement((cur) => (cur ? `${cur}, ${s}` : s))}
                        className="text-xs bg-white border border-amber-300 hover:bg-amber-100 text-amber-800 px-3 py-1 rounded-full"
                      >
                        + {s}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
            <div className="space-y-2">
              <label className="text-xs text-gray-600 font-medium" htmlFor="ai-refinement-input">
                Tilføj detaljer (smag, ingredienser, om den skal indeholde alkohol):
              </label>
              <input
                id="ai-refinement-input"
                data-testid="ai-recipe-refinement-input"
                type="text"
                value={refinement}
                onChange={(e) => setRefinement(e.target.value)}
                placeholder="F.eks. syrlig, jordbær, uden alkohol"
                maxLength={200}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-400"
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && refinement.trim().length > 0) {
                    e.preventDefault();
                    callGenerator(refinement.trim());
                  }
                }}
              />
            </div>
            <div className="flex gap-2 justify-end">
              <Button variant="outline" onClick={handleClose} data-testid="ai-error-close">
                Luk
              </Button>
              <Button
                onClick={() => callGenerator(refinement.trim())}
                data-testid="ai-error-retry"
                className="bg-purple-600 hover:bg-purple-700"
              >
                Prøv igen
              </Button>
            </div>
          </div>
        )}

        {canUseAI && recipe && loadingState === 'idle' && (
          <div className="space-y-4" data-testid="ai-recipe-draft">
            <div>
              <h3 className="text-xl font-bold text-gray-900" data-testid="ai-recipe-name">{recipe.name}</h3>
              <p className="text-sm text-gray-600 mt-1">{recipe.description}</p>
            </div>

            <div className="grid grid-cols-3 gap-2 text-xs">
              <div className="bg-gray-50 rounded-lg p-2 text-center">
                <p className="text-gray-500">Volumen</p>
                <p className="font-semibold">
                  {validation?.total_volume_ml ?? recipe.base_volume_ml ?? 2000} ml
                </p>
              </div>
              <div className="bg-gray-50 rounded-lg p-2 text-center">
                <p className="text-gray-500">
                  Brix{validation ? '' : ' (mål)'}
                </p>
                <p className="font-semibold">
                  {(validation?.total_brix ?? recipe.brix ?? recipe.target_brix ?? 13.0)}°Bx
                </p>
              </div>
              <div className="bg-gray-50 rounded-lg p-2 text-center">
                <p className="text-gray-500">Alkohol</p>
                <p className="font-semibold">
                  {validation
                    ? `${validation.alcohol_percentage}%`
                    : (recipe.contains_alcohol ? 'Ja' : 'Nej')}
                </p>
              </div>
            </div>

            <div>
              <h4 className="font-semibold text-sm text-gray-700 mb-2">Ingredienser</h4>
              <ul className="space-y-1.5 text-sm" data-testid="ai-recipe-ingredients">
                {(recipe.ingredients || []).map((ing, idx) => (
                  <li
                    key={`${ing.name}-${idx}`}
                    className="flex justify-between bg-purple-50/40 rounded px-3 py-1.5"
                  >
                    <span>{ing.name}{ing.required === false ? <span className="text-xs text-gray-400 ml-1">(valgfri)</span> : null}</span>
                    <span className="font-mono text-gray-600">
                      {ing.amount || ing.quantity} {ing.unit || 'ml'}
                    </span>
                  </li>
                ))}
              </ul>
            </div>

            <div>
              <h4 className="font-semibold text-sm text-gray-700 mb-2">Fremgangsmåde</h4>
              <ol className="space-y-1.5 text-sm list-decimal list-inside text-gray-700" data-testid="ai-recipe-steps">
                {(recipe.procedure || recipe.steps || []).map((step, idx) => (
                  <li key={idx}>{step}</li>
                ))}
              </ol>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-800">
              ⚠️ AI-genererede opskrifter er forslag. Tjek altid Brix og mængder før produktion.
            </div>

            {/* Freezability check — total Brix, alcohol %, and slush-machine
                safety. The badge colour matches the worst dimension. */}
            {(validating || validation) && (
              <div
                data-testid="ai-recipe-freezability"
                className={`rounded-lg p-3 border ${
                  validation?.overall_status === 'danger'
                    ? 'bg-red-50 border-red-200'
                    : validation?.overall_status === 'warning'
                    ? 'bg-amber-50 border-amber-200'
                    : validation?.overall_status === 'acceptable'
                    ? 'bg-yellow-50 border-yellow-200'
                    : 'bg-emerald-50 border-emerald-200'
                }`}
              >
                <div className="flex items-center justify-between gap-2 mb-2">
                  <h4 className="text-sm font-semibold text-gray-800">Frysbarheds-tjek</h4>
                  {validating && (
                    <span className="text-xs text-gray-500 flex items-center gap-1">
                      <Loader2 className="w-3 h-3 animate-spin" /> Beregner…
                    </span>
                  )}
                  {validation && !validating && (
                    <span
                      data-testid="ai-recipe-freezability-badge"
                      className={`text-xs font-semibold px-2 py-0.5 rounded-full ${
                        validation.overall_status === 'danger'
                          ? 'bg-red-200 text-red-900'
                          : validation.overall_status === 'warning'
                          ? 'bg-amber-200 text-amber-900'
                          : validation.overall_status === 'acceptable'
                          ? 'bg-yellow-200 text-yellow-900'
                          : 'bg-emerald-200 text-emerald-900'
                      }`}
                    >
                      {validation.overall_status === 'danger' && 'Uden for sikker zone'}
                      {validation.overall_status === 'warning' && 'Advarsel'}
                      {validation.overall_status === 'acceptable' && 'Acceptabel'}
                      {validation.overall_status === 'stable' && 'Stabil'}
                    </span>
                  )}
                </div>
                {validation && (
                  <>
                    <div className="grid grid-cols-2 gap-2 text-xs mb-2">
                      <div className="bg-white/60 rounded px-2 py-1.5">
                        <span className="text-gray-500">Samlet Brix</span>
                        <div className="font-semibold text-gray-900">{validation.total_brix}°Bx</div>
                      </div>
                      <div className="bg-white/60 rounded px-2 py-1.5">
                        <span className="text-gray-500">Alkohol</span>
                        <div className="font-semibold text-gray-900">{validation.alcohol_percentage}%</div>
                      </div>
                    </div>
                    <p className="text-xs text-gray-700 mb-1" data-testid="ai-recipe-brix-message">{validation.brix_message}</p>
                    <p className="text-xs text-gray-700" data-testid="ai-recipe-alcohol-message">{validation.alcohol_message}</p>
                    {validation.suggested_adjustment && (
                      <div className="mt-3 pt-3 border-t border-current/10 flex items-center justify-between gap-3">
                        <p className="text-xs text-gray-800 flex-1">
                          <strong>Forslag:</strong> {validation.suggested_adjustment.explanation}
                        </p>
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={applyAutoAdjustment}
                          data-testid="ai-recipe-auto-adjust"
                          className="bg-white whitespace-nowrap"
                        >
                          Justér automatisk
                        </Button>
                      </div>
                    )}
                  </>
                )}
              </div>
            )}

            <div className="flex flex-col sm:flex-row gap-2 pt-2 border-t">
              <Button
                variant="outline"
                onClick={handleClose}
                disabled={!!saving}
                className="sm:order-1"
                data-testid="ai-recipe-discard"
              >
                <X className="w-4 h-4 mr-1" />
                Kassér
              </Button>
              <Button
                onClick={() => handleSave('private')}
                disabled={!!saving || validating}
                className="bg-purple-600 hover:bg-purple-700 sm:order-2 sm:flex-1"
                data-testid="ai-recipe-save-private"
                title={validating ? 'Venter på frysbarheds-tjek…' : undefined}
              >
                {saving === 'private' ? (
                  <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                ) : (
                  <Save className="w-4 h-4 mr-1" />
                )}
                Gem privat
              </Button>
              {!isGuest && (
                <Button
                  onClick={() => handleSave('sandbox')}
                  disabled={!!saving || validating}
                  className="bg-gradient-to-r from-purple-500 to-pink-500 hover:opacity-90 sm:order-3 sm:flex-1"
                  data-testid="ai-recipe-send-sandbox"
                  title={validating ? 'Venter på frysbarheds-tjek…' : undefined}
                >
                  {saving === 'sandbox' ? (
                    <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                  ) : (
                    <ShieldCheck className="w-4 h-4 mr-1" />
                  )}
                  Send til Sandbox
                </Button>
              )}
            </div>

            {isGuest && (
              <p className="text-xs text-gray-500 text-center">
                Log ind for at sende opskriften til admin-godkendelse (Sandbox).
              </p>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default AIRecipeGeneratorModal;
